import { AbstractModel } from '@patsnap/tahiti-core';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { OarTaskContentModel } from '../oar-task-content-model';
import type { OarTaskContent } from '../../types/oar-types';
import { OarParseStatus } from '../../types/oar-types';

/**
 * 创建初始的 OAR 任务内容状态
 *
 * @returns 初始化的 OarTaskContent 对象
 */
function createInitialOarTaskContent(): OarTaskContent {
    return {
        oarPdfInfo: undefined,
        oarPatentOffice: undefined,
        oarHeaderParseStatus: OarParseStatus.NOT_STARTED,
        oarTableParseStatus: OarParseStatus.NOT_STARTED,
        oarBodyParseStatus: OarParseStatus.NOT_STARTED,
        oarHeaderInfo: undefined,
        oarCitedDocuments: undefined,
        oarConclusiveOpinions: undefined,
        oarExaminerConclusions: undefined,
        oarBodyText: undefined,
        oarBodySections: undefined,
    };
}

/**
 * OAR 任务内容模型实现
 *
 * 管理 OAR（Office Action Response）任务的所有内容数据，包括：
 * - PDF 文件信息
 * - 解析状态（头部、表格、正文）
 * - 解析结果（头部信息、引用文献、结论性意见、正文内容等）
 */
@injectable()
export class OarTaskContentModelImpl extends AbstractModel<OarTaskContent> implements OarTaskContentModel {
    protected _modelData: OarTaskContent = createInitialOarTaskContent();

    /**
     * 克隆数据
     *
     * @param data - 要克隆的数据
     * @returns 克隆后的数据
     */
    // eslint-disable-next-line complexity
    clone(data: OarTaskContent): OarTaskContent {
        return {
            ...data,
            // 深拷贝数组字段
            oarCitedDocuments: data.oarCitedDocuments ? [...data.oarCitedDocuments] : undefined,
            oarConclusiveOpinions: data.oarConclusiveOpinions ? [...data.oarConclusiveOpinions] : undefined,
            oarExaminerConclusions: data.oarExaminerConclusions ? [...data.oarExaminerConclusions] : undefined,
            oarBodySections: data.oarBodySections ? [...data.oarBodySections] : undefined,
            // 深拷贝嵌套对象
            oarPdfInfo: data.oarPdfInfo ? { ...data.oarPdfInfo } : undefined,
            oarHeaderInfo: data.oarHeaderInfo
                ? {
                    ...data.oarHeaderInfo,
                    agency: { ...data.oarHeaderInfo.agency },
                    barcodes: [...data.oarHeaderInfo.barcodes],
                }
                : undefined,
        };
    }

    /**
     * 释放资源
     *
     * 清空所有任务内容数据，恢复到初始状态
     */
    dispose(): void {
        this.update(() => createInitialOarTaskContent());
    }
}
